import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { tenantAuditLogInfiniteQueryOptions } from "@/features/identity-hub/lib/queries/audit-log.queries";
import {
  Archive, Copy, Crown, ExternalLink, FileText, Loader2, MoreHorizontal, Pencil, Plus, Search, Settings, Shield, ShieldPlus, Sparkles, UserCheck, UserPlus, UserX, Users, X,
} from "lucide-react";
import {
  Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import type { AccessGroupItem, UserAccessEntry, UserItem } from "@/features/identity-hub/api/identity-hub.api";
import { orgColor, isSuperTenantMember } from "@/features/identity-hub/utils/iam.utils";
import {
  ORG_ACCENT_DRAWER_TOP_CLASS,
  ORG_ACCENT_HEADER_WASH_CLASS,
  orgAccentVar,
} from "@/features/identity-hub/utils/org-accent-surface";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { industryLabel } from "./OrgCard";
import { AuditTimeline } from "./AuditTimeline";
import { SuperTenantBadge } from "./SuperTenantBadge";
import { OrgLogoMark } from "./OrgLogoMark";
import { UserPhotoMark } from "./UserPhotoMark";
import { useProgressiveList } from "@/hooks/useProgressiveList";
import { ClampedDescription } from "./ClampedDescription";

// ─── Constants ────────────────────────────────────────────────────────────────

// ─── Types ────────────────────────────────────────────────────────────────────

type DrawerTab = "members" | "roles" | "audit";

export interface OrgItem {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  logoUrl?: string | null;
  description?: string | null;
  brandColor?: string | null;
  retoolUserId?: string | null;
  /** ISO timestamp set when the org is soft-deleted; `null` for active orgs. */
  deletedAt?: string | null;
  /** Industry indices assigned to this org (from TenantIndustry join table). */
  industryIndices?: string[];
  /** The org's default industry from tenant_settings.defaultIndustry. */
  defaultIndustry?: string | null;
}

export interface OrgDrawerProps {
  org: OrgItem | null;
  members: UserItem[];
  roles: AccessGroupItem[];
  accessByUser: Map<string, UserAccessEntry[]>;
  allUsers: UserItem[];
  isSuperTenant: boolean;
  onClose: () => void;
  onJumpToPerson: (userId: string) => void;
  onJumpToRole: (roleId: string) => void;
  onRemoveFromOrg: (userId: string) => void;
  onRemoveRole?: (roleId: string) => void;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onAddMember?: (userId: string, accessId: string) => void;
  isAddingMember?: boolean;
  addingMemberId?: string | null;
  addingAccessId?: string | null;
  isRemovingMember?: boolean;
  removingMemberId?: string | null;
  /** Opens the New Role dialog pre-selected to this org. Used by the no-roles empty state. */
  onCreateRole?: (tenantId: string) => void;
  /** Opens the centered Org Settings modal for this org. */
  onOpenSettings?: (tenantId: string) => void;
  onOpenAuditLog?: (options?: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null }) => void;
}

// ─── No-roles empty state ─────────────────────────────────────────────────────
//
// Shown in the Members tab (and reachable via the footer CTA) when an org has
// zero roles defined. We can't grant anyone access without an access group, so
// the only meaningful next action is "Create a role" — surface that boldly
// instead of letting the user open an add-member picker that does nothing.

function NoRolesEmptyState({ onCreateRole }: { onCreateRole?: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-4 px-4 py-8 text-center">
      <div className="relative">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 rounded-2xl bg-primary/10 blur-xl"
        />
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/5 shadow-sm">
          <Shield className="h-6 w-6 text-primary" strokeWidth={1.75} />
        </div>
        <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-primary/30 bg-card shadow-sm">
          <Sparkles className="h-2.5 w-2.5 text-primary" />
        </span>
      </div>

      <div className="flex max-w-[260px] flex-col gap-1.5">
        <p className="text-sm font-semibold leading-snug text-foreground">
          {t("iam.orgDrawer.noRoles.title", "Set up roles to add members")}
        </p>
        <p className="text-[12px] leading-relaxed text-muted-foreground">
          {t(
            "iam.orgDrawer.noRoles.body",
            "Roles define what people can do in this organization. Create your first role, then invite members and pick which one they get.",
          )}
        </p>
      </div>

      {onCreateRole && (
        <Button size="sm" className="gap-1.5" onClick={onCreateRole}>
          <Plus className="h-3.5 w-3.5" />
          {t("iam.orgDrawer.noRoles.cta", "Create first role")}
        </Button>
      )}

      <ol className="mt-1 flex w-full max-w-[260px] flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3 text-left">
        <li className="flex items-start gap-2 text-[11px] text-muted-foreground">
          <span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold tabular-nums text-primary">
            1
          </span>
          <span>
            {t("iam.orgDrawer.noRoles.step1", "Create a role and pick its permissions.")}
          </span>
        </li>
        <li className="flex items-start gap-2 text-[11px] text-muted-foreground">
          <span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold tabular-nums text-muted-foreground">
            2
          </span>
          <span>
            {t("iam.orgDrawer.noRoles.step2", "Add members and assign them that role.")}
          </span>
        </li>
      </ol>
    </div>
  );
}

// ─── Members tab ─────────────────────────────────────────────────────────────

function MembersTab({
  orgId,
  orgName,
  members,
  accessByUser,
  hasRoles,
  onJumpToPerson,
  onRemoveFromOrg,
  isRemovingMember,
  removingMemberId,
  onEditProfile,
  onSuspend,
  onUnsuspend,
  onOpenAuditLog,
  onCreateRole,
}: {
  orgId: string;
  orgName: string;
  members: UserItem[];
  accessByUser: Map<string, UserAccessEntry[]>;
  hasRoles: boolean;
  onJumpToPerson: (userId: string) => void;
  onRemoveFromOrg: (userId: string) => void;
  isRemovingMember?: boolean;
  removingMemberId?: string | null;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onOpenAuditLog?: (user: UserItem) => void;
  onCreateRole?: () => void;
}) {
  const { t } = useTranslation();

  // Progressively reveal members instead of mounting all of them on tab open.
  // Big tenants can have hundreds — initial 30 keeps the drawer responsive,
  // and the IntersectionObserver auto-loads the next batch as the user
  // scrolls within the drawer body.
  const { visibleCount, hasMore, sentinelRef } = useProgressiveList(members.length, {
    initialBatch: 30,
    loadMore: 30,
  });
  const visibleMembers = useMemo(
    () => members.slice(0, visibleCount),
    [members, visibleCount],
  );

  if (members.length === 0) {
    // No-roles empty state — guides the operator through the setup ordering
    // (a role is required before anyone can be granted access).
    if (!hasRoles) {
      return <NoRolesEmptyState onCreateRole={onCreateRole} />;
    }

    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Users className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">{t("iam.orgDrawer.members.empty.title", "No members yet")}</p>
        <p className="text-xs text-muted-foreground">
          {t("iam.orgDrawer.members.empty.body", "Add people to this organization to get started.")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {visibleMembers.map((user) => {
        const entries = (accessByUser.get(user.id) ?? []).filter(
          (e) => e.access.tenantId === orgId,
        );
        const roleNames = entries.map((e) => e.access.name).join(", ");

        return (
          <div
            key={user.id}
            className="group/member flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:bg-muted/40"
          >
            {/* Clickable area → jump to person */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => onJumpToPerson(user.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onJumpToPerson(user.id); } }}
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
            >
              <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
                <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="sm" className="shrink-0" />
              </ImagePreviewTrigger>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-[13px] font-medium leading-tight text-foreground">
                    {user.displayName}
                  </span>
                  {isSuperTenantMember(accessByUser.get(user.id)) && <SuperTenantBadge />}
                </div>
                {roleNames && (
                  <span className="truncate text-[11px] leading-tight text-muted-foreground">
                    {roleNames}
                  </span>
                )}
              </div>
            </div>

            {/* Status badge */}
            <span
              className={cn(
                "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                user.status === "ACTIVE"
                  ? "bg-success-subtle text-success-subtle-foreground"
                  : user.status === "DEACTIVATED"
                  ? "bg-destructive/10 text-destructive"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {t(`iam.personDrawer.status.${user.status}`, user.status)}
            </span>

            {/* Remove from org */}
            {(() => {
              const isLastAccess = user.accessIds.length <= 1;
              const isRemoving = isRemovingMember && removingMemberId === user.id;
              return (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={cn("opacity-0 group-hover/member:opacity-100", isRemoving && "opacity-100")}>
                        <button
                          type="button"
                          onClick={() => !isLastAccess && !isRemoving && onRemoveFromOrg(user.id)}
                          disabled={isLastAccess || isRemoving}
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-opacity disabled:cursor-not-allowed disabled:opacity-40 hover:bg-destructive/10 hover:text-destructive"
                          aria-label={`Remove ${user.displayName} from organization`}
                          aria-busy={isRemoving}
                        >
                          {isRemoving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-destructive" />
                          ) : (
                            <X className="h-3.5 w-3.5 text-destructive" />
                          )}
                        </button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {isLastAccess
                        ? t("iam.roleDrawer.members.lastAccessTooltip", "Can't revoke — this is the user's only access")
                        : t("iam.personDrawer.orgs.removeFromOrg", { org: orgName })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })()}

            {/* Actions dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md opacity-0 transition-opacity hover:bg-muted group-hover/member:opacity-100"
                  aria-label="More actions"
                >
                  <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="gap-2" onClick={() => onEditProfile?.(user)}>
                  <Pencil className="h-3.5 w-3.5" />
                  {t("iam.personDrawer.actions.editProfile")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => navigator.clipboard.writeText(user.email)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {t("iam.personDrawer.actions.copyEmail")}
                </DropdownMenuItem>
                {onOpenAuditLog && (
                  <DropdownMenuItem className="gap-2" onClick={() => onOpenAuditLog(user)}>
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t("iam.personDrawer.actions.viewAuditLog")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {user.status === "DEACTIVATED" ? (
                  <DropdownMenuItem className="gap-2" onClick={() => onUnsuspend?.(user)}>
                    <UserCheck className="h-3.5 w-3.5" />
                    {t("iam.personDrawer.actions.unsuspend")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => onSuspend?.(user)}
                  >
                    <UserX className="h-3.5 w-3.5" />
                    {t("iam.personDrawer.actions.suspend")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })}
      {hasMore && (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center py-2 text-[11px] text-muted-foreground"
        >
          {t("iam.orgDrawer.members.loadingMore", {
            defaultValue: "Loading {{remaining}} more…",
            remaining: members.length - visibleCount,
          })}
        </div>
      )}
    </div>
  );
}

// ─── Roles tab ────────────────────────────────────────────────────────────────

function RolesTab({
  roles,
  onJumpToRole,
  onRemoveRole,
  onCreateRole,
}: {
  roles: AccessGroupItem[];
  onJumpToRole: (roleId: string) => void;
  onRemoveRole?: (roleId: string) => void;
  onCreateRole?: () => void;
}) {
  if (roles.length === 0) {
    return <NoRolesEmptyState onCreateRole={onCreateRole} />;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {roles.map((role) => (
        <div
          key={role.id}
          className="group/role flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:bg-muted/40"
        >
          {/* Clickable area → jump to role */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => onJumpToRole(role.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onJumpToRole(role.id); } }}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
              <Shield className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <span className="flex-1 truncate text-[13px] font-medium text-foreground">{role.name}</span>
          </div>

          {/* Remove role */}
          {onRemoveRole && (
            <button
              type="button"
              onClick={() => onRemoveRole(role.id)}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover/role:opacity-100"
              aria-label={`Delete role ${role.name}`}
            >
              <X className="h-3.5 w-3.5 text-destructive" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── OrgDrawer ────────────────────────────────────────────────────────────────

type AddMemberState =
  | { step: "pick-person" }
  | { step: "pick-role"; userId: string; userName: string }
  | null;

const ANIM_DURATION = 200;

export function OrgDrawer({ org, members, roles, accessByUser, allUsers, isSuperTenant, onClose, onJumpToPerson, onJumpToRole, onRemoveFromOrg, onRemoveRole, onEditProfile, onSuspend, onUnsuspend, onAddMember, isAddingMember, addingMemberId, addingAccessId, isRemovingMember, removingMemberId, onCreateRole, onOpenSettings, onOpenAuditLog }: OrgDrawerProps) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const canViewAuditLog = perms.IDENTITY_HUB_VIEW_AUDIT_LOG.isOn;
  const [tab, setTab] = useState<DrawerTab>("members");
  const [visible, setVisible] = useState(true);
  const [addMemberState, setAddMemberState] = useState<AddMemberState>(null);
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevAddingMember = useRef(false);
  const tabButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const isArchived = !!org?.deletedAt;

  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);

  const eligibleUsers = useMemo(
    () => allUsers.filter((u) => !memberIds.has(u.id)),
    [allUsers, memberIds],
  );

  const filteredEligible = useMemo(() => {
    const q = addMemberSearch.trim().toLowerCase();
    if (!q) return eligibleUsers;
    return eligibleUsers.filter(
      (u) => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [eligibleUsers, addMemberSearch]);

  useEffect(() => {
    if (!org?.id) return;
    clearTimeout(closeTimer.current);
    setVisible(true);
    setTab("members");
    setAddMemberState(null);
    setAddMemberSearch("");
  }, [org?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (prevAddingMember.current && !isAddingMember) {
      setAddMemberState(null);
      setAddMemberSearch("");
    }
    prevAddingMember.current = !!isAddingMember;
  }, [isAddingMember]);

  useLayoutEffect(() => {
    const activeIndex = (["members", "roles", "audit"] as DrawerTab[]).indexOf(tab);
    const el = tabButtonRefs.current[activeIndex];
    if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
  }, [tab]);

  // Audit count for the tab badge — same infinite query the Audit tab uses,
  // so React Query shares the cache and only the first page is fetched here.
  // NOTE: this hook must run on every render (incl. when `org` is null) — early-returning
  // before it would change the hook count and crash with "Rendered fewer hooks than expected".
  const { data: auditData } = useInfiniteQuery({
    ...tenantAuditLogInfiniteQueryOptions(org?.id ?? "", { pageSize: 20 }),
    enabled: canViewAuditLog && !!org,
  });

  if (!org) return null;

  const drawerAccentColor = org.brandColor ?? orgColor(org.name);
  const displayLogoUrl = org.logoUrl ?? null;

  const handleClose = () => {
    setVisible(false);
    closeTimer.current = setTimeout(() => onClose(), ANIM_DURATION);
  };

  const auditCount = auditData?.pages[0]?.items.length ?? undefined;

  const tabs: Array<{ id: DrawerTab; label: string; Icon: typeof Shield; count?: number }> = [
    { id: "members",  label: "Members",  Icon: Users,    count: members.length },
    { id: "roles",    label: "Roles",    Icon: Shield,   count: roles.length },
    { id: "audit",    label: "Audit",    Icon: FileText, count: auditCount },
  ];

  return (
    <>
      {/* Backdrop — visible only on < xl */}
      <div
        className={cn(
          "absolute inset-0 z-[9] bg-black/20 xl:hidden duration-200",
          visible ? "animate-in fade-in" : "animate-out fade-out fill-mode-forwards",
        )}
        onClick={handleClose}
        aria-hidden
      />

      <aside
        className={cn(
          "relative flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card",
          "xl:relative xl:w-[400px] xl:shrink-0",
          "max-xl:absolute max-xl:inset-y-3 max-xl:right-3 max-xl:z-10 max-xl:w-[min(400px,calc(92vw-0.75rem))] max-xl:shadow-[-16px_0_40px_-12px_oklch(0.21_0.01_264/0.25)]",
          "duration-200",
          visible
            ? "animate-in slide-in-from-right-4 fade-in"
            : "animate-out slide-out-to-right-4 fade-out fill-mode-forwards",
          !isSuperTenant && ORG_ACCENT_DRAWER_TOP_CLASS,
        )}
        style={!isSuperTenant ? orgAccentVar(drawerAccentColor) : undefined}
      >
        {/* ── Head ─────────────────────────────────────────── */}
        <div
          className={cn(
            "flex items-start justify-between gap-2.5 border-b border-border/40 px-5 py-4",
            !isSuperTenant && ORG_ACCENT_HEADER_WASH_CLASS,
          )}
          style={!isSuperTenant ? orgAccentVar(drawerAccentColor) : undefined}
        >
          <div className="flex min-w-0 items-start gap-3">
            <ImagePreviewTrigger src={displayLogoUrl} alt={org.name}>
              <OrgLogoMark
                name={org.name}
                logoUrl={displayLogoUrl}
                accent={drawerAccentColor}
                isSuperTenant={isSuperTenant}
                size={36}
              />
            </ImagePreviewTrigger>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="truncate text-[15px] font-semibold leading-tight">{org.name}</h3>
                {isSuperTenant && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600"
                          aria-label={t("iam.orgCard.superTenant.iconLabel", "Platform organization")}
                        >
                          <Crown className="h-2.5 w-2.5" />
                          Adge
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {t("iam.orgCard.superTenant.tooltip", "The platform operator tenant — used for cross-tenant administration and settings managed by Adge.")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {isArchived && (
                  <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    <Archive className="h-2.5 w-2.5" />
                    {t("iam.orgDrawer.archivedBadge", "Archived")}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">{industryLabel(org.type)}</p>
              {org.description && (
                <ClampedDescription text={org.description} maxLines={3} className="mt-1" />
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {onOpenSettings && !isSuperTenant && (
              <button
                type="button"
                onClick={() => onOpenSettings(org.id)}
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={t("iam.orgDrawer.openSettings", "Organization settings")}
              >
                <Settings className="h-3.5 w-3.5" />
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="More actions"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => navigator.clipboard.writeText(org.id)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy ID
                </DropdownMenuItem>
                {onOpenAuditLog && canViewAuditLog && (
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={() => onOpenAuditLog({ searchPrefill: org.name })}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View audit log
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={handleClose}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close panel"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* ── Facts row ────────────────────────────────────── */}
        <div className="grid grid-cols-3 border-b border-border">
          <div className="flex flex-col gap-1 px-4 py-3">
            <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">Members</span>
            <span className="text-sm font-semibold tabular-nums">{members.length}</span>
          </div>
          <div className="flex flex-col gap-1 px-4 py-3">
            <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">Roles</span>
            <span className="text-sm font-semibold tabular-nums">{roles.length}</span>
          </div>
          <div className="flex flex-col gap-1 px-4 py-3">
            <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">Type</span>
            <span className="text-sm font-semibold">{industryLabel(org.type)}</span>
          </div>
        </div>

        {/* ── Tab strip ────────────────────────────────────── */}
        <div className="relative flex border-b border-border px-4">
          {tabs.map((t, i) => (
            <button
              key={t.id}
              ref={(el) => { tabButtonRefs.current[i] = el; }}
              onClick={() => setTab(t.id)}
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-2.5 text-[12px] font-medium transition-colors",
                tab === t.id ? "text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <t.Icon className="h-3 w-3" />
              {t.label}
              {t.count !== undefined && (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 font-mono text-[10px] text-muted-foreground">
                  {t.count}
                </span>
              )}
            </button>
          ))}
          <div
            className="absolute bottom-0 h-0.5 bg-primary"
            style={{
              left: indicator.left,
              width: indicator.width,
              transition: "left 200ms cubic-bezier(0.4,0,0.2,1), width 200ms cubic-bezier(0.4,0,0.2,1)",
            }}
          />
        </div>

        {/* ── Body ─────────────────────────────────────────── */}
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
          {tab === "members" && (
            <MembersTab
              orgId={org.id}
              orgName={org.name}
              members={members}
              accessByUser={accessByUser}
              hasRoles={roles.length > 0}
              onJumpToPerson={onJumpToPerson}
              onRemoveFromOrg={onRemoveFromOrg}
              isRemovingMember={isRemovingMember}
              removingMemberId={removingMemberId}
              onEditProfile={onEditProfile}
              onSuspend={onSuspend}
              onUnsuspend={onUnsuspend}
              onOpenAuditLog={onOpenAuditLog && canViewAuditLog ? (user) => onOpenAuditLog({ searchPrefill: user.email }) : undefined}
              onCreateRole={onCreateRole && !isSuperTenant && !isArchived ? () => onCreateRole(org.id) : undefined}
            />
          )}

          {tab === "roles" && (
            <RolesTab
              roles={roles}
              onJumpToRole={onJumpToRole}
              onRemoveRole={onRemoveRole}
              onCreateRole={onCreateRole && !isSuperTenant && !isArchived ? () => onCreateRole(org.id) : undefined}
            />
          )}

          {tab === "audit" && (
            <AuditTimeline
              scope="tenant"
              scopeId={org.id}
              hasPermission={canViewAuditLog}
              onEntryClick={onOpenAuditLog ? (id) => onOpenAuditLog({ highlightId: id }) : undefined}
            />
          )}
        </div>

        {/* ── Add member panel ──────────────────────────────── */}
        {addMemberState && (
          <div className="animate-in slide-in-from-bottom-2 fade-in duration-200 border-t border-border bg-muted px-4 py-3">
            {addMemberState.step === "pick-person" && (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[11px] font-medium text-muted-foreground">Add a member to {org.name}</p>
                  <button
                    type="button"
                    onClick={() => { setAddMemberState(null); setAddMemberSearch(""); }}
                    className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>

                {/* Search */}
                <div className="relative mb-2">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={addMemberSearch}
                    onChange={(e) => setAddMemberSearch(e.target.value)}
                    placeholder="Search by name or email…"
                    className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-3 text-[12px] outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 placeholder:text-muted-foreground"
                    autoFocus
                  />
                </div>

                {/* User list */}
                <div className="flex max-h-52 flex-col gap-1 overflow-y-auto">
                  {filteredEligible.length === 0 ? (
                    <p className="py-4 text-center text-[11px] text-muted-foreground">
                      {addMemberSearch ? "No people match your search." : "Everyone is already a member."}
                    </p>
                  ) : (
                    filteredEligible.map((user) => {
                      const isAddingUser = isAddingMember && addingMemberId === user.id;
                      return (
                      <div
                        key={user.id}
                        role="button"
                        tabIndex={isAddingMember ? -1 : 0}
                        aria-disabled={isAddingMember}
                        aria-busy={isAddingUser}
                        onClick={() => {
                          if (isAddingMember) return;
                          if (roles.length === 0) return;
                          if (roles.length === 1) {
                            onAddMember?.(user.id, roles[0].id);
                          } else {
                            setAddMemberSearch("");
                            setAddMemberState({ step: "pick-role", userId: user.id, userName: user.displayName });
                          }
                        }}
                        onKeyDown={(e) => {
                          if (isAddingMember) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (roles.length === 0) return;
                            if (roles.length === 1) {
                              onAddMember?.(user.id, roles[0].id);
                            } else {
                              setAddMemberSearch("");
                              setAddMemberState({ step: "pick-role", userId: user.id, userName: user.displayName });
                            }
                          }
                        }}
                        className={cn(
                          "flex cursor-pointer items-center gap-2.5 rounded-lg border border-border px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/5",
                          isAddingMember && "cursor-not-allowed",
                          isAddingUser && "border-primary/40 bg-primary/5",
                          isAddingMember && !isAddingUser && "opacity-50",
                        )}
                      >
                        <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
                          <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="sm" className="shrink-0" />
                        </ImagePreviewTrigger>
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="truncate text-[13px] font-medium leading-tight">{user.displayName}</span>
                          <span className="truncate text-[11px] leading-tight text-muted-foreground">{user.email}</span>
                        </div>
                        <span
                          className={cn(
                            "flex h-5 w-5 shrink-0 items-center justify-center",
                            !isAddingUser && "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                            !isAddingUser && user.status === "ACTIVE"
                              ? "bg-success-subtle text-success-subtle-foreground"
                              : !isAddingUser && "bg-muted text-muted-foreground",
                          )}
                        >
                          {isAddingUser ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                          ) : (
                            t(`iam.personDrawer.status.${user.status}`, user.status)
                          )}
                        </span>
                      </div>
                    );
                    })
                  )}
                </div>
              </>
            )}

            {addMemberState.step === "pick-role" && (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[11px] font-medium text-muted-foreground">
                    Pick a role for <span className="text-foreground">{addMemberState.userName}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => setAddMemberState(null)}
                    className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {roles.map((role) => {
                    const isAddingRole = isAddingMember && addingAccessId === role.id;
                    return (
                    <button
                      key={role.id}
                      type="button"
                      disabled={isAddingMember}
                      aria-busy={isAddingRole}
                      onClick={() => {
                        onAddMember?.(addMemberState.userId, role.id);
                      }}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium transition-colors hover:border-primary/60 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50",
                        isAddingRole && "border-primary/60 bg-primary/10 text-primary",
                      )}
                    >
                      {isAddingRole && <Loader2 className="h-3 w-3 animate-spin" />}
                      {role.name}
                    </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setAddMemberState({ step: "pick-person" })}
                  className="mt-2 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  ← Back
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Footer ───────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          {roles.length === 0 && onCreateRole && !isSuperTenant && !isArchived ? (
            // Members can't be added without at least one role to assign — flip
            // the primary CTA to role creation so the user has an obvious next
            // step instead of a disabled, dead-end button.
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => onCreateRole(org.id)}
            >
              <ShieldPlus className="h-3.5 w-3.5" />
              {t("iam.orgDrawer.noRoles.cta", "Create first role")}
            </Button>
          ) : (
            <Button
              size="sm"
              className="gap-1.5"
              disabled={isSuperTenant || isArchived || isAddingMember || eligibleUsers.length === 0 || roles.length === 0}
              onClick={() => setAddMemberState({ step: "pick-person" })}
            >
              {isAddingMember ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <UserPlus className="h-3.5 w-3.5" />
              )}
              Add member
            </Button>
          )}
        </div>
      </aside>
    </>
  );
}
